import { InfoOutline } from '@mui/icons-material';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Column, Row } from 'further-ui/components/Layout';
import Switch from 'further-ui/components/Switch';
import CustomRequestForm from '../CustomRequestForm';
import ErrorBoundary from 'components/ErrorBoundary';
import BasicCard from '../BasicCard';
import useCustomTaxRequests from './useCustomTaxRequests';
import useOnboardingSettings from 'hooks/lp/data/vehicle/useOnboardingSettings';

type Props = {
  vehicleId: string;
  disabled?: boolean;
};

const TaxSettings: React.FC<Props> = ({ vehicleId, disabled = false }) => {
  const {
    customTaxRequests,
    handleCustomRequestsOrderChange,
    addCustomTaxRequest,
    deleteCustomTaxRequest,
    updateCustomTaxRequest,
    isLoading,
  } = useCustomTaxRequests(vehicleId);
  const { onboardingSettings, updateOnboardingSettings, isUpdating } =
    useOnboardingSettings(vehicleId);

  const enableCustomTaxRequests = onboardingSettings.enableCustomTaxRequests;

  const handleEnableCustomTaxRequestsChange = (value: boolean) => {
    updateOnboardingSettings({
      enableCustomTaxRequests: value,
    });
  };

  return (
    <Column fullWidth spacing="lg">
      <Column spacing="xs" fullWidth>
        <Typography variant="h6">Tax information collected</Typography>
        <Typography variant="body2">
          By default, if tax information is requested as part of an investor
          journey, we collect FATCA, CRS and tax residency information. If you
          wish to collect more information, you can turn on Custom tax requests
          below.
        </Typography>
      </Column>
      <Divider />
      <Row fullWidth centered>
        <Row centered spacing="xxs">
          <Typography variant="h6">Custom tax requests</Typography>
          <Tooltip title="Request custom tax information if you want to gather additional information on top of FATCA, CRS and tax residency data.">
            <InfoOutline fontSize="inherit" />
          </Tooltip>
        </Row>

        <Switch
          checked={enableCustomTaxRequests}
          onChange={handleEnableCustomTaxRequestsChange}
          disabled={disabled || isUpdating}
        />
      </Row>
      {enableCustomTaxRequests ? (
        <>
          <Divider />

          <BasicCard
            title="Custom tax information requests"
            description=" Create and organise your tax information requests into sections
              below."
          />

          <BasicCard
            title="Additional tax information requests"
            description="Please provide the information requested below."
            outlined
          />
          <ErrorBoundary>
            <CustomRequestForm
              disabled={disabled}
              customRequests={customTaxRequests}
              handleCustomRequestsOrderChange={handleCustomRequestsOrderChange}
              addRequest={(values, onSuccess) => {
                addCustomTaxRequest(
                  {
                    vehicleId,
                    ...values,
                  },
                  { onSuccess },
                );
              }}
              updateRequest={(id, values, onSuccess) => {
                updateCustomTaxRequest(
                  {
                    id,
                    vehicleId,
                    ...values,
                  },
                  { onSuccess },
                );
              }}
              deleteRequest={(id) => {
                deleteCustomTaxRequest({
                  id,
                  vehicleId,
                });
              }}
              isLoading={isLoading}
            />
          </ErrorBoundary>
        </>
      ) : null}
    </Column>
  );
};

export default TaxSettings;
